import {
  Box,
  Container,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { unwrapResult } from "@reduxjs/toolkit";
import { PageTitleBar } from "common/components";
import { useTranslation } from "common/locales";
import GenericDropdown from "features/general/GenericDropdown";
import { useAppDispatch, useAppSelector } from "model";
import { getMetabaseUrl } from "model/metabaseUrlSlice";
import { selectAllProjectDashboards } from "model/projectDashboardsSlice";
import { setSelectedProjectDashboard } from "model/selectedProjectDashboard";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

export default function ProjectInsightsDrawer() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const projectDashboards = useAppSelector(selectAllProjectDashboards);
  const selectedDashboard = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-return
    (state) => (state as any).selectedProjectDashboard
  );
  const history = useHistory();
  const [metabaseUrl, setMetabaseUrl] = useState<string>("");
  const dispatch = useAppDispatch();

  const projectDashboardsAsMap: { [key: string]: string } = {};
  projectDashboards.forEach((dashboard) => {
    projectDashboardsAsMap[dashboard.guid] = dashboard.title;
  });

  // Load Metabase URL when dashboard is selected (mobile only)
  useEffect(() => {
    if (isMobile && selectedDashboard && selectedDashboard.guid) {
      const workaroundFunc = async () => {
        const params: { [name: string]: string } = {};
        try {
          const res = await dispatch(
            getMetabaseUrl({
              visualizationId: selectedDashboard.guid,
              params,
            })
          ).then(unwrapResult);
          setMetabaseUrl(res.url);
        } catch (e) {
          console.log(e);
        }
      };
      void workaroundFunc();
    }
  }, [dispatch, selectedDashboard?.guid, selectedDashboard, isMobile]);

  if (!selectedDashboard.guid && projectDashboards.length > 0) {
    // Default to the first dashboard if none is selected
    const firstDashboard = projectDashboards[0];
    if (firstDashboard) {
      dispatch(
        setSelectedProjectDashboard({
          guid: firstDashboard.guid,
          title: firstDashboard.title,
        })
      );
    }
  }

  return (
    <>
      <PageTitleBar title={t("toolbar.projectinsighs.title")} showDivider />
      <Container
        maxWidth="sm"
        sx={{
          pt: isMobile ? 2 : 3,
          pb: isMobile ? 10 : 2, // Add bottom padding for mobile bottom navigation
          height: isMobile ? "calc(100vh - 120px)" : "auto", // Increased height - less space reserved
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Grid item xs={12} sm={6} display="flex" alignItems="baseline">
          <GenericDropdown
            required={false}
            disabled={projectDashboards.length < 1}
            flipKeyValue={true}
            onChange={(event) => {
              dispatch(
                setSelectedProjectDashboard(
                  projectDashboards.filter(
                    (d) => d.guid === event.target.value
                  )[0]
                )
              );
            }}
            currentValue={selectedDashboard.guid}
            label={"projects.dashboard.label"}
            name={`selectedFarmerDashboard`}
            possibleValues={projectDashboardsAsMap}
            tPrefix={"reseedEvent.totalAmountUnit.label"}
            inputError={false}
            shouldTranslate={false}
          />
        </Grid>
        {/* Mobile Dashboard iframe */}
        {isMobile && (
          <Grid
            item
            xs={12}
            sx={{ flex: 1, display: "flex", flexDirection: "column" }}
          >
            {metabaseUrl ? (
              <Box
                sx={{
                  backgroundColor: "white",
                  borderRadius: 2,
                  boxShadow: 1,
                  overflow: "hidden",
                  flex: 1, // Take remaining space
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <iframe
                  frameBorder="0"
                  src={metabaseUrl}
                  width="100%"
                  height="100%"
                  title="Farm-insights"
                  style={{ border: "none", flex: 1 }}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  backgroundColor: "white",
                  borderRadius: 2,
                  p: 3,
                  boxShadow: 1,
                  flex: 1, // Take remaining space
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "calc(100vh - 300px)", // Ensure minimum height even when loading
                }}
              >
                <Typography variant="body1" color="text.secondary">
                  Loading dashboard...
                </Typography>
              </Box>
            )}
          </Grid>
        )}
      </Container>
    </>
  );
}
